import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase/config';
import { UserCheck, Shield, Briefcase, Mail, Lock, UserPlus, LogIn, Eye, EyeOff, Info } from 'lucide-react';
import T from '../T';

const PENDING_APPROVAL_MESSAGE = 'Your account is pending approval from the Admin.';
const IDLE_PASSWORD_CLEAR_MS = 20 * 60 * 1000;

const AuthPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'client' | 'lawyer' | 'admin'>('client');
  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [isAdminOptionVisible, setIsAdminOptionVisible] = useState(false);
  const [isLawyerOptionVisible, setIsLawyerOptionVisible] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const idleTimeoutRef = useRef<number | null>(null);
  const navigate = useNavigate();

  const clearPasswordData = (showSecurityMessage = false) => {
    setPassword('');
    setShowPassword(false);
    if (showSecurityMessage) {
      setInfo('For your security, your password was cleared after 20 minutes of inactivity.');
    }
  };

  const normalizeAuthError = (firebaseCode?: string) => {
    switch (firebaseCode) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return emailExists
        ? 'Incorrect password. If you forgot it, use Forgot Password to reset it.'
        : 'Invalid email or password. Please check your credentials.';
    case 'auth/user-not-found':
      return 'No account was found with this email. Please register to continue.';
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please sign in or reset your password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/network-request-failed':
      return 'Network issue detected. Please check your connection and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/internal-error':
      return emailExists
        ? 'We could not sign you in right now. If you are a lawyer, your account may still be waiting for admin approval. You can also use Forgot Password.'
        : 'We could not complete sign in. If this is a new email, please register first.';
    default:
      return emailExists
        ? 'Sign-in could not be completed. This can happen if your account is waiting for admin approval or if access is temporarily unavailable. Please try Forgot Password or contact support.'
        : 'Sign-in could not be completed because this email is not registered yet. Please register first.';
    }
  };

  const resetIdleTimeout = () => {
    if (idleTimeoutRef.current) {
      window.clearTimeout(idleTimeoutRef.current);
    }

    idleTimeoutRef.current = window.setTimeout(() => {
      clearPasswordData(true);
    }, IDLE_PASSWORD_CLEAR_MS);
  };

  useEffect(() => {
    // Clear password on load/refresh
    clearPasswordData();
    
    const authNotice = sessionStorage.getItem('auth_notice');
    if (authNotice) {
      setError(authNotice);
      sessionStorage.removeItem('auth_notice');
    }

    const fetchSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setIsAdminOptionVisible(data.isAdminOptionVisible ?? false);
          setIsLawyerOptionVisible(data.isLawyerOptionVisible ?? true);
        }
      } catch (err) {
        console.error("Error fetching settings:", err);
      }
    };
    fetchSettings();

    const handleUserActivity = () => {
      resetIdleTimeout();
    };

    const handlePageHide = () => {
      clearPasswordData();
      window.sessionStorage.setItem('auth_last_hidden_at', String(Date.now()));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        window.sessionStorage.setItem('auth_last_hidden_at', String(Date.now()));
      } else {
        const hiddenAtRaw = window.sessionStorage.getItem('auth_last_hidden_at');
        const hiddenAt = hiddenAtRaw ? Number(hiddenAtRaw) : 0;
        if (hiddenAt && Date.now() - hiddenAt >= IDLE_PASSWORD_CLEAR_MS) {
          clearPasswordData(true);
        }
        resetIdleTimeout();
      }
    };

    const activityEvents: Array<keyof WindowEventMap> = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, handleUserActivity));
    window.addEventListener('beforeunload', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    resetIdleTimeout();

    return () => {
      if (idleTimeoutRef.current) {
        window.clearTimeout(idleTimeoutRef.current);
      }
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, handleUserActivity));
      window.removeEventListener('beforeunload', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Check if email exists when it changes
  useEffect(() => {
    const checkEmail = async () => {
      if (email && email.includes('@') && email.includes('.')) {
        try {
          // Use the secure email_index collection instead of a query on users
          const indexDoc = await getDoc(doc(db, 'email_index', email.toLowerCase()));
          setEmailExists(indexDoc.exists());
        } catch (err) {
          console.error("Error checking email index:", err);
          setEmailExists(null);
        }
      } else {
        setEmailExists(null);
      }
    };

    const timer = setTimeout(checkEmail, 500); // Debounce
    return () => clearTimeout(timer);
  }, [email]);

  useEffect(() => {
    if (isRegistering && emailExists === true) {
      setIsRegistering(false);
      setInfo('This email is already registered. You can sign in or use Forgot Password.');
      clearPasswordData();
    }
  }, [emailExists, isRegistering]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');

    if (isRegistering && emailExists === true) {
      setError('This email is already registered. Please sign in or use Forgot Password.');
      setIsRegistering(false);
      clearPasswordData();
      return;
    }

    try {
      if (isRegistering) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const accountStatus = role === 'lawyer' ? 'pending' : 'approved';
        
        // Create the user profile
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: email.toLowerCase(),
          role,
          status: accountStatus,
          assignedLawyerId: null,
          assignedClients: [],
          createdAt: new Date().toISOString()
        });

        // Add to the public email_index so the existence check works for others
        await setDoc(doc(db, 'email_index', email.toLowerCase()), { exists: true });

        if (role === 'lawyer') {
          sessionStorage.setItem('auth_notice', PENDING_APPROVAL_MESSAGE);
          await signOut(auth);
          setIsRegistering(false);
          clearPasswordData();
          navigate('/login');
          return;
        }

        navigate(`/${role}`);
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
        
        // Ensure the email_index is up-to-date even for legacy users who sign in
        await setDoc(doc(db, 'email_index', email.toLowerCase()), { exists: true }, { merge: true });

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userRole = (userData.role || 'client').toLowerCase();
          const accountStatus = (userData.status || 'approved').toLowerCase();

          if (userRole === 'lawyer' && accountStatus !== 'approved') {
            setError('Your lawyer account is waiting for admin approval. You will be able to sign in once approved.');
            await signOut(auth);
            clearPasswordData();
            return;
          }

          navigate(`/${userRole}`);
        }
      }
    } catch (err: any) {
      setError(normalizeAuthError(err?.code));
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setInfo('Password reset email sent! Please check your inbox.');
      setError('');
      clearPasswordData();
    } catch (err: any) {
      setError(normalizeAuthError(err?.code));
    }
  };

  return (
    <div style={{
      minHeight: '80vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '12px',
        boxShadow: 'var(--shadow)',
        width: '100%',
        maxWidth: '500px'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <h2 style={{ color: 'var(--primary-color)', fontSize: '1.8rem' }}>
            <T>{isRegistering ? 'Join Y LIU LAW' : 'Welcome Back'}</T>
          </h2>
          <p style={{ color: '#666', marginTop: '10px' }}>
            <T>{isRegistering ? 'Create your account to access our legal services.' : 'Sign in to your professional legal portal.'}</T>
          </p>
        </div>

        {error && <div style={{ color: 'red', backgroundColor: '#fff5f5', padding: '10px', borderRadius: '4px', marginBottom: '20px', fontSize: '0.9rem', textAlign: 'center', border: '1px solid #ffc1c1' }}>{error}</div>}
        {info && <div style={{ color: '#0d47a1', backgroundColor: '#e3f2fd', padding: '10px', borderRadius: '4px', marginBottom: '20px', fontSize: '0.9rem', textAlign: 'center', border: '1px solid #bbdefb', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}><Info size={18} /> {info}</div>}

        <form onSubmit={handleSubmit} autoComplete="off">
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="login-email" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}><T>Email Address</T></label>
            <div style={{ position: 'relative' }}>
              <Mail style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} size={18} />
              <input 
                id="login-email"
                name="email"
                type="email" 
                required 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 40px',
                  borderRadius: '4px',
                  border: '1px solid rgba(0, 212, 255, 0.35)',
                  boxSizing: 'border-box',
                  backgroundColor: 'rgba(10, 12, 16, 0.82)',
                  color: '#ffffff'
                }}
                placeholder="you@example.com"
              />
            </div>
          </div>

          <div style={{ marginBottom: isRegistering ? '30px' : '15px' }}>
            <label htmlFor="login-password" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}><T>Password</T></label>
            <div style={{ position: 'relative' }}>
              <Lock style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} size={18} />
              <input 
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"} 
                required 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                style={{
                  width: '100%',
                  padding: '12px 45px 12px 40px',
                  borderRadius: '4px',
                  border: '1px solid rgba(0, 212, 255, 0.35)',
                  boxSizing: 'border-box',
                  backgroundColor: 'rgba(10, 12, 16, 0.82)',
                  color: '#ffffff'
                }}
                placeholder="••••••••"
              />
              <button 
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {isRegistering && (
            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}><T>I am joining as a...</T></label>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${1 + (isLawyerOptionVisible ? 1 : 0) + (isAdminOptionVisible ? 1 : 0)}, 1fr)`, gap: '10px' }}>
                <button 
                  type="button"
                  onClick={() => setRole('client')}
                  style={{
                    padding: '15px 10px',
                    borderRadius: '8px',
                    border: `2px solid ${role === 'client' ? 'var(--secondary-color)' : '#eee'}`,
                    backgroundColor: role === 'client' ? '#fff9eb' : 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'var(--transition)'
                  }}
                >
                  <UserCheck size={24} color={role === 'client' ? 'var(--secondary-color)' : '#666'} />
                  <span style={{ fontSize: '0.85rem', fontWeight: role === 'client' ? 'bold' : 'normal' }}><T>Client</T></span>
                </button>
                
                {isLawyerOptionVisible && (
                  <button 
                    type="button"
                    onClick={() => setRole('lawyer')}
                    style={{
                      padding: '15px 10px',
                      borderRadius: '8px',
                      border: `2px solid ${role === 'lawyer' ? 'var(--secondary-color)' : '#eee'}`,
                      backgroundColor: role === 'lawyer' ? '#fff9eb' : 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'var(--transition)'
                    }}
                  >
                    <Briefcase size={24} color={role === 'lawyer' ? 'var(--secondary-color)' : '#666'} />
                    <span style={{ fontSize: '0.85rem', fontWeight: role === 'lawyer' ? 'bold' : 'normal' }}><T>Lawyer</T></span>
                  </button>
                )}

                {isAdminOptionVisible && (
                  <button 
                    type="button"
                    onClick={() => setRole('admin')}
                    style={{
                      padding: '15px 10px',
                      borderRadius: '8px',
                      border: `2px solid ${role === 'admin' ? 'var(--secondary-color)' : '#eee'}`,
                      backgroundColor: role === 'admin' ? '#fff9eb' : 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'var(--transition)'
                    }}
                  >
                    <Shield size={24} color={role === 'admin' ? 'var(--secondary-color)' : '#666'} />
                    <span style={{ fontSize: '0.85rem', fontWeight: role === 'admin' ? 'bold' : 'normal' }}><T>Admin</T></span>
                  </button>
                )}
              </div>
            </div>
          )}

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '14px' }}>
            {isRegistering ? <><UserPlus size={20} /><T>Create Account</T></> : <><LogIn size={20} /><T>Sign In</T></>}
          </button>

          <div style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
            {isRegistering ? (
              <>
                <T>Already have an account?</T>{' '}
                <button 
                  type="button" 
                  onClick={() => setIsRegistering(false)}
                  style={{ background: 'none', color: 'var(--secondary-color)', fontWeight: 'bold', padding: 0, textDecoration: 'underline' }}
                >
                  <T>Sign In</T>
                </button>
                {emailExists === true && (
                  <>
                    {' | '}
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      style={{ background: 'none', color: 'var(--secondary-color)', fontWeight: 'bold', padding: 0, textDecoration: 'underline' }}
                    >
                      <T>Forgot Password?</T>
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                {emailExists === true ? (
                  <div style={{ display: 'grid', gap: '8px', justifyItems: 'center' }}>
                    <span style={{ color: '#555' }}><T>This email is already registered.</T></span>
                    <button 
                      type="button" 
                      onClick={handleResetPassword}
                      style={{ background: 'none', color: 'var(--secondary-color)', fontWeight: 'bold', padding: 0, textDecoration: 'underline' }}
                    >
                      <T>Forgot Password?</T>
                    </button>
                  </div>
                ) : emailExists === false ? (
                  <>
                    <T>Don't have an account?</T>{' '}
                    <button 
                      type="button" 
                      onClick={() => setIsRegistering(true)}
                      style={{ background: 'none', color: 'var(--secondary-color)', fontWeight: 'bold', padding: 0, textDecoration: 'underline' }}
                    >
                      <T>Register Now</T>
                    </button>
                  </>
                ) : (
                  // Initial state or checking
                  <span style={{ opacity: 0.5 }}><T>Enter email to continue...</T></span>
                )}
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthPage;
